import { useEffect, useRef, useState } from "react";

import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import {
  CloseIcon,
  DuplicateIcon,
  PlusIcon,
  TrashIcon,
  searchIcon,
} from "@excalidraw/excalidraw/components/icons";

import {
  DEFAULT_PROJECT,
  getProjects,
  type BoardRecord,
  type BoardCatalog,
} from "../data/BoardStorage";

import "./BoardsDialog.scss";

// ─── Utilities ───────────────────────────────────────────────────────────────

const GRADIENTS = [
  ["#6366f1", "#818cf8"],
  ["#ec4899", "#f472b6"],
  ["#f59e0b", "#fbbf24"],
  ["#10b981", "#34d399"],
  ["#3b82f6", "#60a5fa"],
  ["#8b5cf6", "#a78bfa"],
  ["#ef4444", "#f87171"],
  ["#06b6d4", "#22d3ee"],
];

const PROJECT_DOTS = [
  "#6366f1",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#8b5cf6",
];

const hashStr = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const boardGradient = (id: string): [string, string] => {
  const pair = GRADIENTS[hashStr(id) % GRADIENTS.length];
  return [pair[0], pair[1]];
};

const projectDot = (name: string): string =>
  PROJECT_DOTS[hashStr(name) % PROJECT_DOTS.length];

const timeAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
};

// ─── Icons ───────────────────────────────────────────────────────────────────

const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const MoveIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    <line x1="12" y1="11" x2="12" y2="17"/>
    <polyline points="9 14 12 17 15 14"/>
  </svg>
);

const AllIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);

// ─── Inline editable input ────────────────────────────────────────────────────

const InlineInput = ({
  value: initialValue,
  placeholder,
  className,
  onCommit,
  onCancel,
  autoFocus = true,
}: {
  value: string;
  placeholder?: string;
  className: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
  autoFocus?: boolean;
}) => {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.select();
  }, [autoFocus]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed) onCommit(trimmed);
    else onCancel();
  };

  return (
    <input
      ref={ref}
      className={className}
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
    />
  );
};

// ─── Board Card ──────────────────────────────────────────────────────────────

const BoardCard = ({
  board,
  isActive,
  showProject,
  onSelect,
  onDuplicate,
  onDelete,
  onRename,
  onMoveToProject,
  projects,
}: {
  board: BoardRecord;
  isActive: boolean;
  showProject: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRename?: (name: string) => void;
  onMoveToProject?: (project: string) => void;
  projects: string[];
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const moveMenuRef = useRef<HTMLDivElement>(null);
  const [from, to] = boardGradient(board.id);

  useEffect(() => {
    if (!showMoveMenu) return;
    const handler = (e: MouseEvent) => {
      if (!moveMenuRef.current?.contains(e.target as Node)) {
        setShowMoveMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMoveMenu]);

  const otherProjects = projects.filter((p) => p !== board.project);

  return (
    <div
      className={
        "BoardsDialog__card" + (isActive ? " BoardsDialog__card--active" : "")
      }
    >
      {/* Gradient thumbnail — click to open */}
      <button
        type="button"
        className="BoardsDialog__cardThumb"
        onClick={onSelect}
        aria-label={`Open ${board.name}`}
        style={{ "--grad-from": from, "--grad-to": to } as React.CSSProperties}
      >
        <span className="BoardsDialog__cardThumbPattern" aria-hidden />
        {isActive && (
          <span className="BoardsDialog__cardActiveBadge" aria-label="Active">✓</span>
        )}
      </button>

      {/* Footer: name + actions */}
      <div className="BoardsDialog__cardFooter">
        {isRenaming ? (
          <InlineInput
            value={board.name}
            className="BoardsDialog__cardRenameInput"
            onCommit={(name) => {
              onRename?.(name);
              setIsRenaming(false);
            }}
            onCancel={() => setIsRenaming(false)}
          />
        ) : (
          <button
            type="button"
            className="BoardsDialog__cardName"
            onClick={onSelect}
            title={board.name}
          >
            {board.name}
          </button>
        )}

        <span className="BoardsDialog__cardTime">{timeAgo(board.updatedAt)}</span>

        {showProject && (
          <span
            className="BoardsDialog__cardProject"
            style={{ "--project-dot": projectDot(board.project) } as React.CSSProperties}
          >
            {board.project}
          </span>
        )}

        {/* Actions row — visible on hover */}
        <div className="BoardsDialog__cardActions">
          {onRename && (
            <button
              type="button"
              className="BoardsDialog__cardActionBtn"
              title="Rename"
              onClick={() => setIsRenaming(true)}
            >
              <PencilIcon />
            </button>
          )}

          {onMoveToProject && otherProjects.length > 0 && (
            <div className="BoardsDialog__moveMenu" ref={moveMenuRef}>
              <button
                type="button"
                className="BoardsDialog__cardActionBtn"
                title="Move to project"
                onClick={() => setShowMoveMenu((v) => !v)}
              >
                <MoveIcon />
              </button>
              {showMoveMenu && (
                <div className="BoardsDialog__moveDropdown">
                  <div className="BoardsDialog__moveDropdownLabel">Move to</div>
                  {otherProjects.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="BoardsDialog__moveDropdownItem"
                      onClick={() => {
                        onMoveToProject(p);
                        setShowMoveMenu(false);
                      }}
                    >
                      <span className="BoardsDialog__moveDropdownDot" style={{ background: projectDot(p) }} />
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="BoardsDialog__cardActionBtn"
            title="Duplicate"
            onClick={onDuplicate}
          >
            {DuplicateIcon}
          </button>

          <button
            type="button"
            className="BoardsDialog__cardActionBtn BoardsDialog__cardActionBtn--danger"
            title="Delete board"
            onClick={onDelete}
            disabled={isActive}
          >
            {TrashIcon}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────────────────────

const Sidebar = ({
  projects,
  selected,
  boardCounts,
  onSelect,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
}: {
  projects: string[];
  selected: string | null;
  boardCounts: Record<string, number>;
  onSelect: (project: string | null) => void;
  onCreateProject: (name: string) => void;
  onRenameProject: (oldName: string, newName: string) => void;
  onDeleteProject: (name: string) => void;
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const totalCount = Object.values(boardCounts).reduce((a, b) => a + b, 0);

  return (
    <aside className="BoardsDialog__sidebar">
      {/* Fixed top: label + "All boards" */}
      <div className="BoardsDialog__sidebarTop">
        <p className="BoardsDialog__sidebarLabel">Projects</p>

        <button
          type="button"
          className={
            "BoardsDialog__sidebarItem BoardsDialog__sidebarItem--all" +
            (selected === null ? " BoardsDialog__sidebarItem--active" : "")
          }
          onClick={() => onSelect(null)}
        >
          <span className="BoardsDialog__sidebarItemIcon"><AllIcon /></span>
          <span className="BoardsDialog__sidebarItemName">All boards</span>
          <span className="BoardsDialog__sidebarCount">{totalCount}</span>
        </button>

        <div className="BoardsDialog__sidebarDivider" />
      </div>

      {/* Scrollable middle: project list */}
      <div className="BoardsDialog__sidebarScroll">
        {projects.map((p) => (
          <div key={p} className="BoardsDialog__projectRow">
            {editingProject === p ? (
              <InlineInput
                value={p}
                className="BoardsDialog__sidebarRenameInput"
                onCommit={(name) => {
                  onRenameProject(p, name);
                  setEditingProject(null);
                  if (selected === p) onSelect(name);
                }}
                onCancel={() => setEditingProject(null)}
              />
            ) : (
              <>
                <button
                  type="button"
                  className={
                    "BoardsDialog__sidebarItem" +
                    (selected === p ? " BoardsDialog__sidebarItem--active" : "")
                  }
                  onClick={() => onSelect(p)}
                >
                  <span className="BoardsDialog__sidebarDot" style={{ background: projectDot(p) }} />
                  <span className="BoardsDialog__sidebarItemName">{p}</span>
                  <span className="BoardsDialog__sidebarCount">{boardCounts[p] ?? 0}</span>
                </button>

                <div className="BoardsDialog__projectActions">
                  <button
                    type="button"
                    className="BoardsDialog__projectActionBtn"
                    title="Rename project"
                    onClick={() => setEditingProject(p)}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    type="button"
                    className="BoardsDialog__projectActionBtn BoardsDialog__projectActionBtn--danger"
                    title="Delete project"
                    onClick={() => onDeleteProject(p)}
                  >
                    {TrashIcon}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Fixed bottom: New project */}
      <div className="BoardsDialog__sidebarFooter">
        {isCreating ? (
          <InlineInput
            value=""
            placeholder="Project name…"
            className="BoardsDialog__sidebarRenameInput"
            onCommit={(name) => {
              onCreateProject(name);
              setIsCreating(false);
            }}
            onCancel={() => setIsCreating(false)}
          />
        ) : (
          <button
            type="button"
            className="BoardsDialog__sidebarAddProject"
            onClick={() => setIsCreating(true)}
          >
            <span className="BoardsDialog__sidebarAddIcon" aria-hidden>+</span>
            New project
          </button>
        )}
      </div>
    </aside>
  );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────

type BoardsDialogProps = {
  isOpen: boolean;
  catalog: BoardCatalog;
  onClose: () => void;
  onCreateBoard: (project: string) => void;
  onSelectBoard: (boardId: string) => void;
  onDuplicateBoard: (boardId: string) => void;
  onDeleteBoard: (boardId: string) => void;
  onDeleteProject: (name: string) => void;
  onRenameBoard?: (boardId: string, newName: string) => void;
  onMoveToProject?: (boardId: string, project: string) => void;
  onRenameProject?: (oldName: string, newName: string) => void;
};

export const BoardsDialog = ({
  isOpen,
  catalog,
  onClose,
  onCreateBoard,
  onSelectBoard,
  onDuplicateBoard,
  onDeleteBoard,
  onDeleteProject,
  onRenameBoard,
  onMoveToProject,
  onRenameProject,
}: BoardsDialogProps) => {
  const [query, setQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { boards, activeBoardId } = catalog;
  const projects = getProjects(catalog);

  useEffect(() => {
    if (isOpen) setQuery("");
  }, [isOpen]);

  const boardCounts: Record<string, number> = {};
  for (const b of boards) {
    boardCounts[b.project] = (boardCounts[b.project] ?? 0) + 1;
  }

  // Reset selection if the viewed project disappears
  useEffect(() => {
    if (selectedProject !== null && !projects.includes(selectedProject)) {
      setSelectedProject(null);
    }
  }, [projects, selectedProject]);

  const visibleBoards = boards
    .filter((b) => {
      if (selectedProject !== null && b.project !== selectedProject) return false;
      if (query.trim()) return b.name.toLowerCase().includes(query.trim().toLowerCase());
      return true;
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);

  if (!isOpen) return null;

  // Creating a project immediately creates a board in it — projects are just tags.
  const handleCreateProject = (name: string) => {
    onCreateBoard(name);
    setSelectedProject(name);
  };

  return (
    <Dialog
      className="BoardsDialog"
      size="wide"
      title={false}
      onCloseRequest={onClose}
      closeOnClickOutside={true}
    >
      <div className="BoardsDialog__layout">
        <Sidebar
          projects={projects}
          selected={selectedProject}
          boardCounts={boardCounts}
          onSelect={setSelectedProject}
          onCreateProject={handleCreateProject}
          onRenameProject={onRenameProject ?? (() => {})}
          onDeleteProject={onDeleteProject}
        />

        <div className="BoardsDialog__main">
          <div className="BoardsDialog__mainHeader">
            <div className="BoardsDialog__mainTitleRow">
              <div className="BoardsDialog__mainTitle">
                {selectedProject ?? "All boards"}
                <span className="BoardsDialog__mainCount">{visibleBoards.length}</span>
              </div>
              <button type="button" className="BoardsDialog__closeBtn" onClick={onClose} aria-label="Close">
                {CloseIcon}
              </button>
            </div>
            <div className="BoardsDialog__mainToolbar">
              <div className="BoardsDialog__searchWrap">
                <span className="BoardsDialog__searchIcon">{searchIcon}</span>
                <input
                  ref={searchRef}
                  type="search"
                  className="BoardsDialog__searchInput"
                  placeholder="Search boards…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <FilledButton
                variant="filled"
                color="primary"
                size="medium"
                label="New board"
                icon={PlusIcon}
                onClick={() => onCreateBoard(selectedProject ?? DEFAULT_PROJECT)}
              >
                New board
              </FilledButton>
            </div>
          </div>

          <div className="BoardsDialog__grid" role="list">
            {visibleBoards.length ? (
              visibleBoards.map((board) => (
                <BoardCard
                  key={board.id}
                  board={board}
                  isActive={board.id === activeBoardId}
                  showProject={selectedProject === null}
                  projects={projects}
                  onSelect={() => { onSelectBoard(board.id); onClose(); }}
                  onDuplicate={() => onDuplicateBoard(board.id)}
                  onDelete={() => onDeleteBoard(board.id)}
                  onRename={onRenameBoard ? (name) => onRenameBoard(board.id, name) : undefined}
                  onMoveToProject={onMoveToProject ? (p) => onMoveToProject(board.id, p) : undefined}
                />
              ))
            ) : (
              <div className="BoardsDialog__empty">
                {query ? <>No boards match &ldquo;{query}&rdquo;</> : <>No boards in this project yet.</>}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
